import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  formatFinancialYearLabel,
  readStoredFinancialYearStartYear,
  getFinancialYearDropdownOptions,
  getFinancialYearMonthSequence,
  FY_MONTH_ORDER_SHORT,
} from "../utils/financialYear";
import { APP_BRANCHES } from "../config/appBranches";
import { fetchEmployeeGroupMonthlyReport, listDistinctEmployeesForFy } from "../services/partyBrandMonthlySalesService";
import "./MonthlySaleReportPage.css";
import "./PartyBrandMonthlySalesReportPage.css";

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function empListCacheKey(fyStartYear, branchFilter) {
  return `empGroupMonthlyEmpList:${fyStartYear}:${branchFilter || "all"}`;
}

function readEmpListCache(fyStartYear, branchFilter) {
  try {
    const raw = sessionStorage.getItem(empListCacheKey(fyStartYear, branchFilter));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.names) || !parsed.names.length) return null;
    return parsed.names.map(String).filter(Boolean);
  } catch {
    return null;
  }
}

function writeEmpListCache(fyStartYear, branchFilter, names) {
  try {
    sessionStorage.setItem(
      empListCacheKey(fyStartYear, branchFilter),
      JSON.stringify({ names: names || [], at: Date.now() })
    );
  } catch {
    /* ignore quota */
  }
}

function readParams(searchParams) {
  const fyStartYear = Number(searchParams.get("fy")) || readStoredFinancialYearStartYear();
  const employee = searchParams.get("employee") || searchParams.get("party") || "";
  const branch = searchParams.get("branch") || "all";
  return { fyStartYear, employee, branchFilter: branch };
}

function queryString(fyStartYear, employee, branchFilter) {
  const q = new URLSearchParams({ fy: String(fyStartYear), branch: branchFilter });
  if (employee) q.set("employee", employee);
  return q.toString();
}

function monthHeaders(fyStartYear) {
  const seq = getFinancialYearMonthSequence(fyStartYear);
  return seq.map((s, i) => {
    const short = FY_MONTH_ORDER_SHORT[i];
    const yy = String(s.calendarYear).slice(-2);
    return `${short} '${yy}`;
  });
}

function exportExcel(report, fyLabel, branchFilter, headers) {
  if (!report?.groups?.length && !report?.brands?.length) {
    toast.error("Nothing to export");
    return;
  }
  const rowsData = report.groups?.length ? report.groups : report.brands;
  const emp = report.employeeName || report.partyName || "";
  const header = ["Group", "Comm %", ...headers, "Sale total", "Commission total"];
  const body = rowsData.map((r) => [
    r.group || r.brand,
    Math.round((r.commissionPercent || 0) * 100) / 100,
    ...r.months.map((n) => Math.round(n * 100) / 100),
    Math.round(r.total * 100) / 100,
    Math.round((r.commissionTotal || 0) * 100) / 100,
  ]);
  body.push([
    "TOTAL",
    "",
    ...report.monthTotals.map((n) => Math.round(n * 100) / 100),
    Math.round(report.grandTotal * 100) / 100,
    Math.round((report.grandCommission || 0) * 100) / 100,
  ]);
  const sheet = XLSX.utils.aoa_to_sheet([
    [`Employee: ${emp}`],
    [`FY: ${fyLabel}`, `Branch: ${branchFilter === "all" ? "All" : branchFilter}`],
    [`Total commission: ${Math.round((report.grandCommission || 0) * 100) / 100}`],
    [],
    header,
    ...body,
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, "Group monthly");
  const safe = String(emp || "employee").replace(/[\\/:*?"<>|]/g, "_").slice(0, 40);
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buf], { type: "application/octet-stream" }), `Employee-Group-Monthly-${safe}-${fyLabel}.xlsx`);
}

export default function PartyBrandMonthlySalesReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = readParams(searchParams);

  const [fyStartYear, setFyStartYear] = useState(initial.fyStartYear);
  const [branchFilter, setBranchFilter] = useState(initial.branchFilter);
  const [employeeInput, setEmployeeInput] = useState(initial.employee);
  const [employeeName, setEmployeeName] = useState("");
  const [employees, setEmployees] = useState(() => readEmpListCache(initial.fyStartYear, initial.branchFilter) || []);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesProgress, setEmployeesProgress] = useState("");
  const [report, setReport] = useState(null);
  const [monthStats, setMonthStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [hasRun, setHasRun] = useState(false);

  const loadGen = useRef(0);
  const empListGen = useRef(0);

  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const fySelectOptions = useMemo(() => getFinancialYearDropdownOptions(fyStartYear), [fyStartYear]);
  const headers = useMemo(() => monthHeaders(fyStartYear), [fyStartYear]);

  const filteredEmployees = useMemo(() => {
    const q = employeeInput.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((n) => n.toLowerCase().includes(q));
  }, [employees, employeeInput]);

  useEffect(() => {
    setSearchParams(queryString(fyStartYear, employeeName || employeeInput.trim(), branchFilter), {
      replace: true,
    });
  }, [fyStartYear, employeeName, employeeInput, branchFilter, setSearchParams]);

  useEffect(() => {
    setReport(null);
    setMonthStats([]);
    setEmployeeName("");
    setHasRun(false);
  }, [fyStartYear, branchFilter]);

  useEffect(() => {
    const cached = readEmpListCache(fyStartYear, branchFilter);
    if (cached?.length) {
      setEmployees(cached);
      setEmployeesLoading(false);
      setEmployeesProgress("");
      return;
    }

    const gen = ++empListGen.current;
    setEmployees([]);
    setEmployeesLoading(true);
    setEmployeesProgress("Loading employees 0 / 12…");

    listDistinctEmployeesForFy(fyStartYear, branchFilter, (done, total, count) => {
      if (gen !== empListGen.current) return;
      setEmployeesProgress(`Loading employees ${done} / ${total} · ${count} names…`);
    })
      .then((names) => {
        if (gen !== empListGen.current) return;
        setEmployees(names);
        writeEmpListCache(fyStartYear, branchFilter, names);
        if (!names.length) {
          toast("No employee names found in sales import for this FY / branch", { duration: 4000 });
        }
      })
      .catch((e) => {
        if (gen !== empListGen.current) return;
        console.error(e);
        toast.error(e?.message || "Failed to load employee list");
        setEmployees([]);
      })
      .finally(() => {
        if (gen === empListGen.current) {
          setEmployeesLoading(false);
          setEmployeesProgress("");
        }
      });
  }, [fyStartYear, branchFilter]);

  const runReport = useCallback(async () => {
    const trimmed = employeeInput.trim();
    if (!trimmed) {
      toast.error("Select or type an employee / party name");
      return;
    }

    const gen = ++loadGen.current;
    setLoading(true);
    setProgress("Loading month 1 / 12…");
    setHasRun(true);
    setEmployeeName(trimmed);
    setReport(null);
    setMonthStats([]);

    try {
      const out = await fetchEmployeeGroupMonthlyReport({
        fyStartYear,
        employeeName: trimmed,
        branchFilter,
        onProgress: (done, total, lines) => {
          if (gen !== loadGen.current) return;
          setProgress(
            `Loading month ${done} / ${total}${lines != null ? ` · ${Number(lines).toLocaleString()} lines` : ""}…`
          );
        },
      });
      if (gen !== loadGen.current) return;

      setReport(out.report);
      setMonthStats(out.monthStats || []);
      const lines = out.report?.lineCount || 0;
      if (lines > 0) {
        toast.success(
          `${trimmed}: ${lines.toLocaleString()} lines · commission ₹ ${fmt(out.report.grandCommission || 0)}`
        );
      } else {
        toast(`No sales for “${trimmed}” in FY ${formatFinancialYearLabel(fyStartYear)}`, {
          duration: 4000,
        });
      }
    } catch (e) {
      if (gen !== loadGen.current) return;
      console.error(e);
      toast.error(e?.message || String(e));
      setReport(null);
      setMonthStats([]);
    } finally {
      if (gen === loadGen.current) {
        setLoading(false);
        setProgress("");
      }
    }
  }, [employeeInput, fyStartYear, branchFilter]);

  const groupRows = report?.groups?.length ? report.groups : report?.brands || [];

  /** Month slots with any sales (print drops empty months for A4 fit). */
  const activeMonthIndices = useMemo(() => {
    const totals = report?.monthTotals;
    if (!Array.isArray(totals)) return [];
    return totals.map((v, i) => (Number(v) > 0 ? i : -1)).filter((i) => i >= 0);
  }, [report]);

  const handlePrint = () => {
    if (!report || !groupRows.length) {
      toast.error("Load a report first");
      return;
    }
    window.print();
  };

  return (
    <>
      <div className="egm-no-print">
        <MenuBar />
        <HeaderBar />
      </div>

      <main className="msr-upload-page">
        <header className="msr-upload-page-head egm-no-print">
          <div>
            <p className="msr-upload-page-kicker">Reports</p>
            <h1 className="msr-upload-page-title">Employee sales — group × month</h1>
            <p className="msr-upload-page-sub">
              Select <strong>employee</strong> from the list (loads in the background), then{" "}
              <strong>Show report</strong> for sales + commission. Commission rates: select same party under{" "}
              <Link to="/group-commission-settings" className="text-indigo-600 underline">
                Party discount / commission
              </Link>
              .
            </p>
          </div>
        </header>

        <div className="msr-toolbar msr-upload-toolbar egm-no-print">
          <label className="msr-toolbar-label">
            Financial year{" "}
            <select
              className="msr-toolbar-select"
              value={fyStartYear}
              onChange={(e) => setFyStartYear(Number(e.target.value))}
              disabled={loading}
            >
              {fySelectOptions.map(({ startYear, label }) => (
                <option key={startYear} value={startYear}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="msr-toolbar-label">
            Branch{" "}
            <select
              className="msr-toolbar-select"
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              disabled={loading}
            >
              <option value="all">All branches</option>
              {APP_BRANCHES.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <label className="msr-toolbar-label flex-1 min-w-[14rem]">
            Employee{" "}
            <select
              className="msr-toolbar-select w-full max-w-md"
              value={employees.includes(employeeInput) ? employeeInput : ""}
              onChange={(e) => setEmployeeInput(e.target.value)}
              disabled={loading || employeesLoading}
            >
              <option value="">
                {employeesLoading
                  ? employeesProgress || "Loading employees…"
                  : employees.length
                    ? `— Select employee (${employees.length}) —`
                    : "— No employees found —"}
              </option>
              {(employeeInput && !employees.includes(employeeInput) ? [employeeInput, ...employees] : employees).map(
                (p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                )
              )}
            </select>
          </label>
          <label className="msr-toolbar-label flex-1 min-w-[12rem]">
            Or type{" "}
            <input
              className="msr-toolbar-select w-full max-w-md"
              list="employee-group-monthly-list"
              value={employeeInput}
              onChange={(e) => setEmployeeInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !loading && runReport()}
              placeholder={employeesLoading ? "Loading list…" : "Type to filter / search…"}
              disabled={loading}
              autoComplete="off"
            />
            <datalist id="employee-group-monthly-list">
              {filteredEmployees.slice(0, 200).map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </label>
          <button
            type="button"
            className="msr-print-btn"
            onClick={runReport}
            disabled={loading || !employeeInput.trim()}
          >
            {loading ? "Loading…" : "Show report"}
          </button>
          {groupRows.length > 0 && !loading && (
            <>
              <button
                type="button"
                className="msr-print-btn"
                onClick={() => exportExcel(report, fyLabel, branchFilter, headers)}
              >
                Excel
              </button>
              <button type="button" className="msr-print-btn" onClick={handlePrint} title="Print A4 landscape">
                Print (A4)
              </button>
            </>
          )}
        </div>

        <div className="egm-no-print">
          {employeesLoading ? (
            <p className="text-xs text-slate-500 mb-2">{employeesProgress || "Loading employee list…"}</p>
          ) : employees.length > 0 ? (
            <p className="text-xs text-slate-500 mb-2">
              {employees.length.toLocaleString()} employees in list (FY {fyLabel}
              {branchFilter !== "all" ? ` · ${branchFilter}` : ""}). Report data loads only when you click Show
              report.
            </p>
          ) : null}

          {!hasRun && !loading ? (
            <p className="text-sm text-slate-600">
              Choose an employee → <strong>Show report</strong> to load their group × month sales for FY {fyLabel}.
            </p>
          ) : null}

          {loading ? <p className="text-sm text-slate-500">{progress || "Loading…"}</p> : null}

          {!loading && hasRun && report && groupRows.length === 0 ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              No sale lines for <strong>{employeeName}</strong> in FY {fyLabel}
              {branchFilter !== "all" ? ` · ${branchFilter}` : ""}. Name must match import staff / employee column.
            </p>
          ) : null}
        </div>

        {!loading && report && groupRows.length > 0 ? (
          <div className="egm-print-root">
            <h2 className="egm-print-title msr-precalc-title">{report.employeeName || report.partyName}</h2>
            <p className="egm-print-meta msr-precalc-meta">
              Employee sales — group × month · FY {fyLabel}
              {branchFilter !== "all" ? ` · ${branchFilter}` : " · All branches"} ·{" "}
              {report.lineCount.toLocaleString()} sale lines · {groupRows.length} groups · months with sales:{" "}
              {report.monthsWithData}/12
              <span className="egm-print-only">
                {" "}
                · print:{" "}
                {activeMonthIndices.map((i) => headers[i]).join(", ") || "—"} only
              </span>
            </p>

            <div className="egm-print-totals msr-precalc-totals-grid mb-4">
              <div className="msr-precalc-total-box">
                <span>Total sales</span>
                <strong>₹ {fmt(report.grandTotal)}</strong>
              </div>
              <div className="msr-precalc-total-box">
                <span>Total commission</span>
                <strong>₹ {fmt(report.grandCommission || 0)}</strong>
              </div>
            </div>

            <div className="egm-print-hide msr-precalc-panel mb-4 egm-no-print">
              {report.monthsWithData < 12 && (
                <p className="text-xs text-slate-500 mt-1">
                  Only months with uploaded sales import (and this employee’s sales) show amounts.
                </p>
              )}
              {monthStats.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    Import month scan (chunks in Firestore)
                  </p>
                  <table className="msr-precalc-table text-[10px]">
                    <thead>
                      <tr>
                        <th className="text-left">Month</th>
                        <th className="text-right">Doc</th>
                        <th className="text-right">Chunks</th>
                        <th className="text-right">Sale lines (all)</th>
                        <th className="text-right">This employee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthStats.map((m) => (
                        <tr key={m.monthId} className={!m.monthExists ? "text-amber-700" : ""}>
                          <td className="text-left">
                            {m.label} '{String(m.calendarYear).slice(-2)}
                          </td>
                          <td className="text-right font-mono">{m.monthExists ? m.monthId : "missing"}</td>
                          <td className="text-right">{m.chunkCount}</td>
                          <td className="text-right">{m.saleRows}</td>
                          <td className="text-right font-semibold">{m.kept}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <section className="msr-precalc-panel egm-print-table-wrap overflow-x-auto">
              <table className="msr-precalc-table egm-print-table text-xs">
                <thead>
                  <tr>
                    <th className="text-left sticky left-0 bg-slate-50 z-[1] egm-col-group">Group</th>
                    <th className="text-right whitespace-nowrap egm-col-pct">Discount %</th>
                    {headers.map((m, i) => (
                      <th
                        key={m}
                        className={`text-right whitespace-nowrap${
                          Number(report.monthTotals?.[i] || 0) > 0 ? "" : " egm-empty-month"
                        }`}
                      >
                        {m}
                      </th>
                    ))}
                    <th className="text-right whitespace-nowrap">Sale total</th>
                    <th className="text-right whitespace-nowrap">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {groupRows.map((row) => (
                    <tr key={row.group || row.brand}>
                      <td className="text-left font-medium sticky left-0 bg-white z-[1] egm-col-group">
                        {row.group || row.brand}
                      </td>
                      <td className="text-right tabular-nums whitespace-nowrap text-slate-600 egm-col-pct">
                        {Number(row.commissionPercent || 0).toFixed(2)}%
                      </td>
                      {row.months.map((v, i) => (
                        <td
                          key={i}
                          className={`text-right tabular-nums whitespace-nowrap${
                            Number(report.monthTotals?.[i] || 0) > 0 ? "" : " egm-empty-month"
                          }`}
                        >
                          {v > 0 ? fmt(v) : "—"}
                        </td>
                      ))}
                      <td className="text-right font-semibold tabular-nums whitespace-nowrap">{fmt(row.total)}</td>
                      <td className="text-right font-semibold tabular-nums whitespace-nowrap text-emerald-800 egm-comm">
                        {fmt(row.commissionTotal || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td className="text-left sticky left-0 bg-slate-100 z-[1]" colSpan={2}>
                      Total sales
                    </td>
                    {report.monthTotals.map((v, i) => (
                      <td
                        key={i}
                        className={`text-right tabular-nums whitespace-nowrap bg-slate-100${
                          Number(v) > 0 ? "" : " egm-empty-month"
                        }`}
                      >
                        {v > 0 ? fmt(v) : "—"}
                      </td>
                    ))}
                    <td className="text-right tabular-nums whitespace-nowrap bg-slate-100">{fmt(report.grandTotal)}</td>
                    <td className="text-right tabular-nums whitespace-nowrap bg-slate-100 text-emerald-900 egm-comm">
                      {fmt(report.grandCommission || 0)}
                    </td>
                  </tr>
                  <tr className="font-semibold text-emerald-900">
                    <td className="text-left sticky left-0 bg-emerald-50 z-[1]" colSpan={2}>
                      Total commission
                    </td>
                    {(report.monthCommissionTotals || []).map((v, i) => (
                      <td
                        key={i}
                        className={`text-right tabular-nums whitespace-nowrap bg-emerald-50${
                          Number(report.monthTotals?.[i] || 0) > 0 ? "" : " egm-empty-month"
                        }`}
                      >
                        {v > 0 ? fmt(v) : "—"}
                      </td>
                    ))}
                    <td className="bg-emerald-50" />
                    <td className="text-right tabular-nums whitespace-nowrap bg-emerald-50 egm-comm">
                      {fmt(report.grandCommission || 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </section>
            <p className="egm-print-footer egm-no-print text-xs text-slate-400 mt-2">
              <strong>Print (A4)</strong> — landscape; empty months are hidden so the table fits. Print dialog:
              Landscape, Size A4.
            </p>
            <p className="egm-print-footer egm-print-only">
              Printed {new Date().toLocaleString("en-IN")} · Empty months omitted · Commission = party group % ×
              sales
            </p>
          </div>
        ) : null}
      </main>
    </>
  );
}
